/**
 * Misconception Registry — Central catalog of known misconceptions.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §10.2 — Centralized registry with module-prefix
 * convention (rek.*, spr.*, etc.) to prevent synonyms and enable governance
 * validation of misconception tags in seeds.
 *
 * Usage:
 * - Seed authors reference misconception IDs from this registry
 * - Governance pipeline validates that tags in seeds exist in this registry
 * - Admin UI can display human-readable labels for misconception tags
 */

export interface MisconceptionDefinition {
  /** Machine-readable ID (e.g., 'rek.carry-forgotten') */
  id: string
  /** Human-readable label (Dutch) */
  label: string
  /** Module scope */
  module: string
  /** Brief description of the misconception */
  description: string
  /** Common question families where this misconception appears */
  typicalFamilies?: string[]
}

/**
 * Central misconception registry.
 * Organized by module prefix for clarity.
 */
export const MISCONCEPTION_REGISTRY: MisconceptionDefinition[] = [
  // ── Basisrekenen (REK) ──────────────────────────────────────────────────
  {
    id: 'rek.carry-forgotten',
    label: 'Onthouden vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet de overdracht (carry) bij optellen of vermenigvuldigen.',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.borrow-forgotten',
    label: 'Lenen vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet te lenen bij aftrekken, trekt kleinste van grootste af per kolom.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.order-of-operations',
    label: 'Volgorde verkeerd',
    module: 'Basisrekenen',
    description:
      'Leerling past de bewerkingsvolgorde (Meneer Van Dansen) niet correct toe.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.multiplication-table-error',
    label: 'Tafelfout',
    module: 'Basisrekenen',
    description:
      'Leerling kent een specifieke tafel niet uit het hoofd (bijv. 7×8=54).',
    typicalFamilies: ['REK-TAFELS-BASIS', 'REK-TAFELS-UITBREIDEN'],
  },
  {
    id: 'rek.division-remainder-ignored',
    label: 'Rest genegeerd',
    module: 'Basisrekenen',
    description:
      'Leerling negeert de rest bij een deling of rondt verkeerd af.',
    typicalFamilies: ['REK-CIJF-DEEL'],
  },
  {
    id: 'rek.subtract-reversed',
    label: 'Aftrekken omgedraaid',
    module: 'Basisrekenen',
    description:
      'Leerling trekt het kleinste cijfer van het grootste af per kolom, ongeacht boven/onder.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.negative-written',
    label: 'Negatief getal opgeschreven',
    module: 'Basisrekenen',
    description:
      'Leerling schrijft een negatief getal op in plaats van te lenen bij aftrekken.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.column-skipped',
    label: 'Kolom overgeslagen',
    module: 'Basisrekenen',
    description: 'Leerling slaat een kolom over bij cijferend rekenen.',
    typicalFamilies: ['REK-CIJF-MIN', 'REK-CIJF-PLUS'],
  },
  {
    id: 'rek.carry-wrong-value',
    label: 'Verkeerd getal onthouden',
    module: 'Basisrekenen',
    description:
      'Leerling onthoudt het verkeerde getal bij de overdracht (bijv. eenheden i.p.v. tientallen).',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.left-to-right',
    label: 'Links-naar-rechts zonder voorrang',
    module: 'Basisrekenen',
    description:
      'Leerling rekent van links naar rechts zonder de bewerkingsvolgorde toe te passen.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.brackets-ignored',
    label: 'Haakjes genegeerd',
    module: 'Basisrekenen',
    description:
      'Leerling negeert haakjes en past de standaard voorrangsregels toe.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.context-direction-wrong',
    label: 'Verkeerde bewerking bij verhaaltjessom',
    module: 'Basisrekenen',
    description:
      'Leerling kiest de verkeerde bewerking (bijv. optellen i.p.v. aftrekken) bij een contextvraag.',
    typicalFamilies: ['REK-CONTEXT-PLUS-MIN', 'REK-CONTEXT-KEER-DEEL'],
  },
  {
    id: 'rek.context-step-forgotten',
    label: 'Stap vergeten bij meerstapsprobleem',
    module: 'Basisrekenen',
    description:
      'Leerling berekent slechts een deel van een meerstapsprobleem en vergeet de laatste stap.',
    typicalFamilies: ['REK-CONTEXT-PLUS-MIN', 'REK-CONTEXT-MEERSTAPS'],
  },
  {
    id: 'rek.rounding-wrong-direction',
    label: 'Verkeerd afgerond bij schatten',
    module: 'Basisrekenen',
    description:
      'Leerling rondt af naar een niet-dichtstbijzijnd getal bij het schatten.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.strategy-wrong-correction',
    label: 'Verkeerde correctie bij compenseren',
    module: 'Basisrekenen',
    description:
      'Leerling corrigeert de verkeerde kant op na compenseren (bijv. trekt af i.p.v. optellen).',
    typicalFamilies: ['REK-HANDIG-PLUS-MIN', 'REK-HANDIG-KEER'],
  },
  {
    id: 'rek.partial-calculation',
    label: 'Onvolledige berekening',
    module: 'Basisrekenen',
    description:
      'Leerling stopt halverwege de berekening en geeft een tussenresultaat als eindantwoord.',
    typicalFamilies: ['REK-CONTEXT-PLUS-MIN', 'REK-TAFELS-TOEPASSEN'],
  },
  {
    id: 'rek.operation-confused',
    label: 'Bewerkingen verward',
    module: 'Basisrekenen',
    description:
      'Leerling verwart bewerkingen (bijv. vermenigvuldigt i.p.v. optellen, of deelt i.p.v. aftrekken).',
    typicalFamilies: ['REK-CONTEXT-KEER-DEEL', 'REK-GEMENGD-BASIS'],
  },
  {
    id: 'rek.strategy-suboptimal',
    label: 'Kiest niet-handige strategie',
    module: 'Basisrekenen',
    description:
      'Leerling kiest een strategie die wel werkt maar niet handig is (bijv. cijferend i.p.v. factoren herkennen).',
    typicalFamilies: [
      'REK-HANDIG-KEER',
      'REK-HANDIG-DEEL',
      'REK-HANDIG-PLUS-MIN',
    ],
  },
  {
    id: 'rek.calculation-error',
    label: 'Rekenfout',
    module: 'Basisrekenen',
    description:
      'Leerling maakt een rekenfout bij vermenigvuldigen, delen of een tussenstap (niet gerelateerd aan een specifieke misconceptie).',
    typicalFamilies: [
      'REK-CONTEXT-KEER-DEEL',
      'REK-HANDIG-KEER',
      'REK-HANDIG-DEEL',
      'REK-GEMENGD-TOETS',
    ],
  },
  {
    id: 'rek.context-wrong-data',
    label: 'Verkeerde gegevens uit context',
    module: 'Basisrekenen',
    description:
      'Leerling pakt het verkeerde getal uit de opgave (bijv. verkeerd tarief, verkeerd aantal, verkeerde deler).',
    typicalFamilies: ['REK-CONTEXT-KEER-DEEL', 'REK-GEMENGD-TOETS'],
  },

  // ── Getalbegrip (GET) ───────────────────────────────────────────────────
  {
    id: 'get.place-value-swap',
    label: 'Plaatswaarde verwisseld',
    module: 'Getalbegrip',
    description:
      'Leerling verwisselt tientallen en eenheden, of honderdtallen en tientallen.',
    typicalFamilies: ['GET-GROOT-LEZEN', 'GET-GROOT-POS'],
  },
  {
    id: 'get.decimal-align',
    label: 'Komma niet uitgelijnd',
    module: 'Getalbegrip',
    description:
      'Leerling lijnt kommagetallen niet uit bij optellen/aftrekken.',
    typicalFamilies: ['GET-KOMMA-PLUS', 'GET-KOMMA-MIN'],
  },
  {
    id: 'get.fraction-whole-comparison',
    label: 'Breuk als geheel getal vergeleken',
    module: 'Getalbegrip',
    description:
      'Leerling vergelijkt teller en noemer apart i.p.v. de breukwaarde.',
    typicalFamilies: ['GET-BREUK-BASIS', 'GET-VERGELIJK'],
  },
  {
    id: 'get.percentage-of-vs-increase',
    label: 'Percentage van vs. toename',
    module: 'Getalbegrip',
    description:
      'Leerling verwart "X% van" met "X% toename" (bijv. 20% van 100 vs. 100 + 20%).',
    typicalFamilies: ['GET-PROC-VAN', 'GET-PROC-TOENAME'],
  },
  {
    id: 'get.negative-subtraction-direction',
    label: 'Negatief aftrekken omgedraaid',
    module: 'Getalbegrip',
    description:
      'Leerling draait de richting om bij aftrekken met negatieve getallen.',
    typicalFamilies: ['GET-NEGATIEF-MIN'],
  },

  // ── Spelling Regels (SPR) ───────────────────────────────────────────────
  {
    id: 'spr.dt-confusion',
    label: 'D/T-fout',
    module: 'SpellingRegel',
    description:
      'Leerling past de d/t-regel verkeerd toe bij werkwoordsvormen.',
    typicalFamilies: ['SPR-WEET-D-T', 'SPR-WEET-DT'],
  },
  {
    id: 'spr.double-consonant',
    label: 'Verdubbeling vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de medeklinkerverdubbeling (bijv. "baken" i.p.v. "bakken").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.ei-ij-confusion',
    label: 'Ei/ij verwisseld',
    module: 'SpellingRegel',
    description: 'Leerling verwisselt ei en ij in woorden.',
    typicalFamilies: ['SPR-WEET-EI-IJ'],
  },
  {
    id: 'spr.open-closed-syllable',
    label: 'Open/gesloten lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling herkent het verschil niet tussen open en gesloten lettergrepen.',
    typicalFamilies: ['SPR-KLANK-JAGER', 'SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.au-ou-confusion',
    label: 'Au/ou verwisseld',
    module: 'SpellingRegel',
    description: 'Leerling verwisselt au en ou in woorden.',
    typicalFamilies: ['SPR-KLANK-AU-OU'],
  },
  {
    id: 'spr.loanword-phonetic-dutch',
    label: 'Leenwoord fonetisch vernederlandst',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft een leenwoord fonetisch zoals het in het Nederlands klinkt, in plaats van de oorspronkelijke spelling te behouden (bijv. "djiens" i.p.v. "jeans").',
    typicalFamilies: ['SPR-LEEN-ENGELS', 'SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.loanword-partial-phonetic',
    label: 'Leenwoord deels fonetisch',
    module: 'SpellingRegel',
    description:
      'Leerling past een deel van het leenwoord fonetisch aan maar behoudt een ander deel (bijv. "highlite" i.p.v. "highlight").',
    typicalFamilies: ['SPR-LEEN-ENGELS'],
  },
  {
    id: 'spr.linking-n-forgotten',
    label: 'Tussen-n vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de tussen-n (meervouds-en) in een samenstelling (bijv. "pannekoek" i.p.v. "pannenkoek").',
    typicalFamilies: ['SPR-SAM-TUSSEN-N'],
  },
  {
    id: 'spr.linking-n-singular',
    label: 'Enkelvoud i.p.v. meervoud in samenstelling',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt het enkelvoud in plaats van het meervoud als eerste deel van de samenstelling (bijv. "pankoek" i.p.v. "pannenkoek").',
    typicalFamilies: ['SPR-SAM-TUSSEN-N'],
  },
  {
    id: 'spr.compound-split',
    label: 'Samenstelling los geschreven',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft een samenstelling als twee losse woorden (bijv. "pannen koek" i.p.v. "pannenkoek").',
    typicalFamilies: ['SPR-SAM-TUSSEN-N', 'SPR-SAM-TUSSEN-S'],
  },
  {
    id: 'spr.linking-s-forgotten',
    label: 'Tussen-s vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de tussen-s in een samenstelling (bijv. "dorpplein" i.p.v. "dorpsplein").',
    typicalFamilies: ['SPR-SAM-TUSSEN-S'],
  },
  {
    id: 'spr.linking-s-confused-en',
    label: 'Tussen-s verward met tussen-en',
    module: 'SpellingRegel',
    description:
      'Leerling verwart een samenstelling met tussen-s met een samenstelling met tussen-en, of omgekeerd.',
    typicalFamilies: ['SPR-SAM-TUSSEN-S', 'SPR-SAM-TUSSEN-N'],
  },
  {
    id: 'spr.trema-forgotten',
    label: 'Trema vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de trema bij klinkerbotsing in het meervoud (bijv. "bacterien" i.p.v. "bacteriën").',
    typicalFamilies: ['SPR-TREMA-MV'],
  },
  {
    id: 'spr.trema-apostrophe',
    label: 'Apostrof i.p.v. trema',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt een apostrof in plaats van een trema bij het meervoud (bijv. "bacterie\'s" i.p.v. "bacteriën").',
    typicalFamilies: ['SPR-TREMA-MV'],
  },
  {
    id: 'spr.trema-extra-vowel',
    label: 'Extra klinker bij trema',
    module: 'SpellingRegel',
    description:
      'Leerling voegt een extra klinker toe bij het meervoud met trema (bijv. "bacterieën" i.p.v. "bacteriën").',
    typicalFamilies: ['SPR-TREMA-MV'],
  },
  {
    id: 'spr.accent-forgotten',
    label: 'Accent vergeten',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet het accent op een Frans leenwoord of klemtoonwoord (bijv. "resume" i.p.v. "résumé").',
    typicalFamilies: ['SPR-ACCENT'],
  },
  {
    id: 'spr.accent-partial',
    label: 'Accent maar op één positie',
    module: 'SpellingRegel',
    description:
      'Leerling plaatst het accent op slechts één van meerdere vereiste posities (bijv. "résume" of "resumé" i.p.v. "résumé").',
    typicalFamilies: ['SPR-ACCENT'],
  },
  {
    id: 'spr.accent-type-confused',
    label: 'Accenttype verward',
    module: 'SpellingRegel',
    description:
      'Leerling verwart accent aigu (é), accent grave (è), accent circumflex (ê) en trema (ë) met elkaar.',
    typicalFamilies: ['SPR-ACCENT'],
  },
  {
    id: 'spr.latin-plural-english-s',
    label: 'Engels meervoud i.p.v. Latijns',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt het Engelse meervoud met -s in plaats van het Latijnse meervoud (bijv. "museums" i.p.v. "musea").',
    typicalFamilies: ['SPR-LEEN-LATIJN'],
  },
  {
    id: 'spr.latin-plural-dutch-en',
    label: 'Nederlands meervoud i.p.v. Latijns',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt het Nederlandse meervoud met -en in plaats van het Latijnse meervoud (bijv. "museumen" i.p.v. "musea").',
    typicalFamilies: ['SPR-LEEN-LATIJN'],
  },
  {
    id: 'spr.latin-plural-apostrophe',
    label: 'Apostrof-meervoud bij Latijns woord',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt een apostrof voor het meervoud van een Latijns woord (bijv. "museum\'s" i.p.v. "musea").',
    typicalFamilies: ['SPR-LEEN-LATIJN'],
  },
  {
    id: 'spr.greek-origin-confused',
    label: 'Griekse herkomst verward',
    module: 'SpellingRegel',
    description:
      'Leerling verwart Griekse leenwoorden met Franse of Engelse leenwoorden, of herkent Griekse woorddelen niet.',
    typicalFamilies: ['SPR-LEEN-GRIEKS'],
  },
  {
    id: 'spr.greek-spelling-english',
    label: 'Engelse spelling bij Grieks woord',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt de Engelse spellingvariant van een Grieks woord in het Nederlands (bijv. "filosofy" i.p.v. "filosofie", of behoudt "ph" i.p.v. "f").',
    typicalFamilies: ['SPR-LEEN-GRIEKS'],
  },

  // ── SPR: C-uitspraak (leenwoorden) ──
  {
    id: 'spr.c-klank-k-als-s',
    label: 'K-klank als S-klank gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat C vóór a, o of u als S klinkt (terwijl het K is).',
    typicalFamilies: ['SPR-LEEN-C-S-K'],
  },
  {
    id: 'spr.c-klank-s-als-k',
    label: 'S-klank als K-klank gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat C vóór e of i als K klinkt (terwijl het S is).',
    typicalFamilies: ['SPR-LEEN-C-S-K'],
  },
  {
    id: 'spr.c-altijd-k',
    label: 'Denkt C altijd K',
    module: 'SpellingRegel',
    description: 'Leerling denkt dat de letter C altijd als K klinkt.',
    typicalFamilies: ['SPR-LEEN-C-S-K'],
  },
  {
    id: 'spr.c-altijd-s',
    label: 'Denkt C altijd S',
    module: 'SpellingRegel',
    description: 'Leerling denkt dat de letter C altijd als S klinkt.',
    typicalFamilies: ['SPR-LEEN-C-S-K'],
  },
  {
    id: 'spr.c-niet-leenwoord',
    label: 'C-woord niet als leenwoord herkend',
    module: 'SpellingRegel',
    description:
      'Leerling herkent niet dat woorden met C leenwoorden zijn die hun originele spelling behouden.',
    typicalFamilies: ['SPR-LEEN-C-S-K'],
  },
  {
    id: 'spr.c-klank-onhoorbaar',
    label: 'Denkt C onhoorbaar',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de C in bepaalde woorden niet hoorbaar is.',
    typicalFamilies: ['SPR-LEEN-C-S-K'],
  },

  // ── SPR: Au/ou ──
  {
    id: 'spr.au-ou-verward',
    label: 'Au en ou verward',
    module: 'SpellingRegel',
    description:
      'Leerling verwart au-woorden met ou-woorden of omgekeerd (weetwoorden).',
    typicalFamilies: ['SPR-KLANK-AU-OU'],
  },

  // ── SPR: Lange klinkers (aa/ee/oo) ──
  {
    id: 'spr.dubbel-klinker-open',
    label: 'Dubbele klinker in open lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft dubbele klinker (aa, ee, oo) in een open lettergreep waar één klinker volstaat.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.dubbel-klinker-en-medeklinker',
    label: 'Dubbele klinker én medeklinker',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt zowel de klinker als de medeklinker (bijv. "maannen" i.p.v. "manen").',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.maan-man-verward',
    label: 'Maan/man meervoud verward',
    module: 'SpellingRegel',
    description:
      'Leerling verwart het meervoud van "maan" (manen, jager-regel) met "man" (mannen, bakker-regel).',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },

  // ── SPR: Hoofdletters ──
  {
    id: 'spr.hoofdletter-dag',
    label: 'Hoofdletter bij dag',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat dagen van de week een hoofdletter krijgen (Engels-invloed).',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-znw',
    label: 'Hoofdletter bij gewoon znw',
    module: 'SpellingRegel',
    description:
      'Leerling geeft gewone zelfstandige naamwoorden een hoofdletter (Duits-invloed).',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-ww',
    label: 'Hoofdletter bij werkwoord',
    module: 'SpellingRegel',
    description: 'Leerling geeft werkwoorden onterecht een hoofdletter.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-zinsbegin-vergeten',
    label: 'Hoofdletter zinsbegin vergeten',
    module: 'SpellingRegel',
    description: 'Leerling vergeet de hoofdletter aan het begin van een zin.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-familienaam',
    label: 'Hoofdletter bij familieaanduiding',
    module: 'SpellingRegel',
    description:
      'Leerling geeft familieaanduidingen (oma, opa) onterecht een hoofdletter.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-geen-nodig',
    label: 'Denkt geen hoofdletters nodig',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat er geen hoofdletters nodig zijn in een zin.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-zinsbegin-onterecht',
    label: 'Zinsbegin-hoofdletter als fout gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de hoofdletter aan het zinsbegin onterecht is.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-naam-onterecht',
    label: 'Naam-hoofdletter als fout gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de hoofdletter bij een persoonsnaam onterecht is.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },
  {
    id: 'spr.hoofdletter-plaats-onterecht',
    label: 'Plaats-hoofdletter als fout gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de hoofdletter bij een plaatsnaam onterecht is.',
    typicalFamilies: ['SPR-HOOFDLETTER'],
  },

  // ── SPR: Leestekens ──
  {
    id: 'spr.leesteken-punt-ipv-komma',
    label: 'Punt i.p.v. komma',
    module: 'SpellingRegel',
    description:
      'Leerling kiest een punt waar een komma past (bij verbonden zinsdelen).',
    typicalFamilies: ['SPR-LEESTEKEN-PUNT-KOMMA'],
  },
  {
    id: 'spr.leesteken-uitroep-verkeerd',
    label: 'Uitroepteken bij mededeling',
    module: 'SpellingRegel',
    description: 'Leerling kiest een uitroepteken bij een gewone mededeling.',
    typicalFamilies: ['SPR-LEESTEKEN-PUNT-KOMMA'],
  },
  {
    id: 'spr.leesteken-dubbele-punt-verkeerd',
    label: 'Dubbele punt verkeerd gebruikt',
    module: 'SpellingRegel',
    description: 'Leerling kiest een dubbele punt waar een komma of punt past.',
    typicalFamilies: ['SPR-LEESTEKEN-PUNT-KOMMA'],
  },

  // ── SPR: Franse leenwoorden ──
  {
    id: 'spr.leen-fonetisch-schrijven',
    label: 'Leenwoord fonetisch geschreven',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft een Frans leenwoord fonetisch (bijv. "kado" i.p.v. "cadeau").',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-dubbele-klinker',
    label: 'Dubbele klinker bij leenwoord',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt een klinker voor de lange klank bij een leenwoord (bijv. "kadoo").',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-half-frans',
    label: 'Half-Franse spelling',
    module: 'SpellingRegel',
    description:
      'Leerling herkent het begin van een Frans woord maar niet de uitgang (bijv. "cadoo").',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-oir-als-oar',
    label: 'Schrijft -oir als -oar',
    module: 'SpellingRegel',
    description: 'Leerling schrijft de Franse uitgang -oir als -oar.',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-fonetisch-trotwar',
    label: 'Trottoir fonetisch geschreven',
    module: 'SpellingRegel',
    description: 'Leerling schrijft trottoir fonetisch als "trotwar".',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-verkeerde-uitgang-uar',
    label: 'Verzint uitgang -uar',
    module: 'SpellingRegel',
    description:
      'Leerling verzint een niet-bestaande uitgang -uar bij een Frans leenwoord.',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-au-als-ou',
    label: 'Schrijft -au- als -ou-',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft de Franse -au- als -ou- (bijv. "restourant").',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-ant-als-and',
    label: 'Schrijft -ant als -and',
    module: 'SpellingRegel',
    description: 'Leerling schrijft de Franse uitgang -ant als -and.',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-au-weggelaten',
    label: 'Laat -au- weg',
    module: 'SpellingRegel',
    description:
      'Leerling laat de -au- lettercombinatie weg in een Frans leenwoord.',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-age-als-asje',
    label: 'Schrijft -age als -asje',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft de Franse uitgang -age fonetisch als -asje.',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-age-als-aazje',
    label: 'Schrijft -age als -aazje',
    module: 'SpellingRegel',
    description: 'Leerling schrijft de Franse uitgang -age als -aazje.',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-dubbele-r',
    label: 'Verdubbelt r bij leenwoord',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt de r in een Frans leenwoord (bijv. "garrage").',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-balkon-als-frans',
    label: 'Balkon als Frans gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "balkon" een Frans leenwoord is (is Italiaans).',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-hotel-als-frans',
    label: 'Hotel als Frans gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "hotel" een Frans leenwoord is (is Latijn).',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-uitzicht-als-leen',
    label: 'Uitzicht als leenwoord gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "uitzicht" een leenwoord is (is Nederlands).',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-parasol-als-nl',
    label: 'Parasol als Nederlands gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "parasol" een Nederlands woord is (is Frans).',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-terras-als-nl',
    label: 'Terras als Nederlands gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "terras" een Nederlands woord is (is Frans).',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },
  {
    id: 'spr.leen-brochure-als-nl',
    label: 'Brochure als Nederlands gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "brochure" een Nederlands woord is (is Frans).',
    typicalFamilies: ['SPR-LEEN-FRANS'],
  },

  // ── SPR-KLANK-VERDUBBEL / BAKKER misconceptions ────────────────────────
  {
    id: 'spr.single-consonant-long-vowel',
    label: 'Enkele medeklinker geeft lange klank',
    module: 'SpellingRegel',
    description:
      'Leerling vergeet de medeklinker te verdubbelen, waardoor de klank lang wordt (bijv. "katen" i.p.v. "katten").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.wrong-syllable-split',
    label: 'Verkeerde lettergreepverdeling',
    module: 'SpellingRegel',
    description:
      'Leerling verdeelt het woord verkeerd in lettergrepen (bijv. "ka-tten" i.p.v. "kat-ten").',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.double-consonant-correct',
    label: 'Correct verdubbeld woord als fout gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat een correct verdubbeld woord (bommen, netten) fout is.',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.vowel-doubled-instead',
    label: 'Klinker verdubbeld i.p.v. medeklinker',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt de klinker in plaats van de medeklinker (bijv. "baakken" i.p.v. "bakken").',
    typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.triple-consonant',
    label: 'Drie medeklinkers i.p.v. twee',
    module: 'SpellingRegel',
    description:
      'Leerling schrijft drie medeklinkers in plaats van twee (bijv. "kopppen" i.p.v. "koppen").',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.double-consonant-for-long-vowel',
    label: 'Verdubbeling bij lange klank (niet nodig)',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt de medeklinker ook bij woorden met een lange klank (bijv. denkt dat "boom → boommen").',
    typicalFamilies: ['SPR-KLANK-VERDUBBEL'],
  },
  {
    id: 'spr.english-ck-spelling',
    label: 'Engelse ck-spelling toegepast',
    module: 'SpellingRegel',
    description:
      'Leerling past de Engelse ck-spelling toe in het Nederlands (bijv. "bakcker" i.p.v. "bakker").',
    typicalFamilies: ['SPR-KLANK-BAKKER'],
  },
  {
    id: 'spr.wrong-vowel-stem',
    label: 'Verkeerde klinker uit stam',
    module: 'SpellingRegel',
    description:
      'Leerling gebruikt de verkeerde klinker uit de stam bij verbuiging.',
    typicalFamilies: ['SPR-KLANK-BAKKER'],
  },

  // ── SPR-KLANK-VERKORTEN misconceptions ──────────────────────────────────
  {
    id: 'spr.vowel-doubled-in-open-syllable',
    label: 'Dubbele klinker behouden in open lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling behoudt de dubbele klinker in een open lettergreep (bijv. "boomen" i.p.v. "bomen").',
    typicalFamilies: ['SPR-KLANK-VERKORTEN', 'SPR-KLANK-JAGER'],
  },
  {
    id: 'spr.short-vowel-doubling-confused',
    label: 'Korte klank verward met lange klank',
    module: 'SpellingRegel',
    description:
      'Leerling verwart het meervoud van woorden met korte en lange klanken (bijv. "mannen" vs "manen").',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.no-plural-suffix',
    label: 'Meervoudsuitgang vergeten',
    module: 'SpellingRegel',
    description: 'Leerling vergeet de meervoudsuitgang -en toe te voegen.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.short-vowel-confused',
    label: 'Denkt dat de klinker kort is',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat een lange klinker in een open lettergreep kort is.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.consonant-causes-long-vowel',
    label: 'Denkt dat medeklinker de lange klank veroorzaakt',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de medeklinker achter de klinker de lange klank veroorzaakt, i.p.v. de open lettergreep.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },
  {
    id: 'spr.word-class-determines-rule',
    label: 'Denkt dat woordsoort de regel bepaalt',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de spellingregel afhangt van de woordsoort (werkwoord, zelfstandig naamwoord) i.p.v. het type lettergreep.',
    typicalFamilies: ['SPR-KLANK-VERKORTEN'],
  },

  // ── SPR-KLANK-AA-EE-OO additional misconceptions ───────────────────────
  {
    id: 'spr.bomen-fout-gedacht',
    label: 'Correct gespeld woord als fout gezien',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat correct gespeld "bomen" fout is en "boomen" moet zijn.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.tweeklank-verward',
    label: 'Tweeklank verward met lange klinker',
    module: 'SpellingRegel',
    description:
      'Leerling verwart een tweeklank (oe, eu, ui) met een lange klinker (oo, ee, aa).',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.ramen-dubbel',
    label: 'Denkt dat ramen dubbele aa nodig heeft',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat "ramen" als "raamen" geschreven moet worden.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.dubbel-ee-open',
    label: 'Dubbele ee in open lettergreep',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat de dubbele "ee" nodig is in een open lettergreep (bijv. "beenen" i.p.v. "benen").',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.dubbel-klinker-en-medeklinker-ee',
    label: 'Verdubbelt zowel ee als medeklinker',
    module: 'SpellingRegel',
    description:
      'Leerling verdubbelt zowel de klinker als de medeklinker (bijv. "beennen").',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.bakker-jager-verward',
    label: 'Bakker-regel en jager-regel verward',
    module: 'SpellingRegel',
    description:
      'Leerling verwart de bakker-regel (korte klank, dubbele medeklinker) met de jager-regel (lange klank, enkele medeklinker).',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.correct-als-fout',
    label: 'Correct woord als fout gezien',
    module: 'SpellingRegel',
    description: 'Leerling denkt dat een correct gespeld woord fout is.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.betekenis-vs-spelling',
    label: 'Betekenisfout verward met spellingfout',
    module: 'SpellingRegel',
    description: 'Leerling verwart een betekenisfout met een spellingfout.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.medeklinker-focus',
    label: 'Focust op medeklinkers i.p.v. klinkers',
    module: 'SpellingRegel',
    description:
      'Leerling focust op medeklinkerfouten terwijl de klinkerregel het probleem is.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.open-dubbel-verward',
    label: 'Open lettergreep vereist twee klinkers',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat je in een open lettergreep twee klinkers naast elkaar schrijft.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.altijd-dubbel',
    label: 'Lange klinkers altijd dubbel',
    module: 'SpellingRegel',
    description:
      'Leerling denkt dat lange klinkers altijd dubbel geschreven worden, ongeacht het type lettergreep.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },
  {
    id: 'spr.open-gesloten-omgedraaid',
    label: 'Open en gesloten lettergreep omgedraaid',
    module: 'SpellingRegel',
    description:
      'Leerling verwisselt de regels voor open en gesloten lettergrepen.',
    typicalFamilies: ['SPR-KLANK-AA-EE-OO'],
  },

  // ── Spelling Werkwoorden (SWW) ──────────────────────────────────────────
  {
    id: 'sww.kofschip-misapplied',
    label: 'Kofschip verkeerd toegepast',
    module: 'SpellingWW',
    description:
      'Leerling past de kofschip-regel verkeerd toe bij voltooid deelwoord.',
    typicalFamilies: ['SWW-VD-KOF', 'SWW-VT-ZWAK-KOF'],
  },
  {
    id: 'sww.stem-extraction-error',
    label: 'Stam verkeerd bepaald',
    module: 'SpellingWW',
    description:
      'Leerling bepaalt de stam van het werkwoord verkeerd (bijv. "leeft" → stam "leef" i.p.v. "leev").',
    typicalFamilies: ['SWW-TT-STAM', 'SWW-TT-DSTAM'],
  },
  {
    id: 'sww.past-participle-ge-omitted',
    label: 'Ge- weggelaten',
    module: 'SpellingWW',
    description:
      'Leerling vergeet ge- bij het voltooid deelwoord (of voegt het onterecht toe).',
    typicalFamilies: ['SWW-VD-ZONDER-GE'],
  },
  {
    id: 'sww.irregular-verb-regularized',
    label: 'Onregelmatig werkwoord vervoegd als regelmatig',
    module: 'SpellingWW',
    description:
      'Leerling past regelmatige vervoegingsregels toe op een onregelmatig werkwoord.',
    typicalFamilies: ['SWW-VT-ONREGELMATIG', 'SWW-VD-STERK'],
  },

  // ── Metriek (MET) ──────────────────────────────────────────────────────
  {
    id: 'met.unit-factor-10',
    label: 'Factor 10 fout',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt met de verkeerde factor van 10 bij eenheidsomrekening.',
    typicalFamilies: ['MET-LEN-OMREKEN', 'MET-GEW-OMREKEN', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.area-perimeter-confused',
    label: 'Oppervlakte/omtrek verward',
    module: 'Metriek',
    description:
      'Leerling verwart oppervlakte met omtrek (bijv. l×b i.p.v. 2×(l+b)).',
    typicalFamilies: ['MET-OPP-RECHT', 'MET-OMTREK-RECHT'],
  },
  {
    id: 'met.scale-direction',
    label: 'Schaal verkeerde richting',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt i.p.v. deelt (of omgekeerd) bij schaalberekeningen.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },
  {
    id: 'met.angle-type-confused',
    label: 'Hoeksoort verward',
    module: 'Metriek',
    description:
      'Leerling verwart scherpe, stompe, rechte of gestrekte hoeken (bijv. 55° als stomp bestempeld).',
    typicalFamilies: ['MET-HOEK-SOORTEN'],
  },
  {
    id: 'met.angle-rotation-confused',
    label: 'Draaihoek verward',
    module: 'Metriek',
    description:
      'Leerling verwart kwart/halve/hele draai met het verkeerde aantal graden (bijv. kwart draai = 180°).',
    typicalFamilies: ['MET-HOEK-SOORTEN', 'MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.scale-unit-conversion',
    label: 'Eenheid vergeten om te rekenen bij schaal',
    module: 'Metriek',
    description:
      'Leerling vergeet cm naar m of km om te rekenen na de schaalberekening.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },
  {
    id: 'met.scale-area-not-squared',
    label: 'Schaalfactor niet gekwadrateerd bij oppervlakte',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt oppervlakte met de schaalfactor i.p.v. met het kwadraat ervan. Bijv. ×100 i.p.v. ×10.000 bij schaal 1:100.',
    typicalFamilies: ['MET-SCHAAL-OPP'],
  },
  {
    id: 'met.symmetry-line-count',
    label: 'Aantal symmetrielijnen verward',
    module: 'Metriek',
    description:
      'Leerling verwart het aantal symmetrielijnen van verschillende figuren (bijv. vierkant=4, rechthoek=2, gelijkzijdige driehoek=3).',
    typicalFamilies: ['MET-SYMMETRIE'],
  },
  {
    id: 'met.symmetry-mirror-axis',
    label: 'Spiegelas verward',
    module: 'Metriek',
    description:
      'Leerling spiegelt over de verkeerde as (bijv. horizontaal i.p.v. verticaal) of verwart x- en y-coördinaat bij spiegeling.',
    typicalFamilies: ['MET-SYMMETRIE'],
  },

  // ── Tijd (TIJD) ─────────────────────────────────────────────────────────
  {
    id: 'tijd.speed-formula-confused',
    label: 'Snelheidsformule verward',
    module: 'Tijd',
    description:
      'Leerling gebruikt de verkeerde bewerking (bijv. vermenigvuldigen in plaats van delen) bij de AST-driehoek.',
    typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.minutes-to-hours-error',
    label: 'Minuten naar uren fout',
    module: 'Tijd',
    description:
      'Leerling rekent minuten verkeerd om naar decimale uren (bijv. 15 min = 0,15 uur in plaats van 0,25 uur).',
    typicalFamilies: ['TIJD-SNELHEID', 'TIJD-AFSTAND', 'TIJD-GEMIDDELDE'],
  },
  {
    id: 'tijd.timezone-direction-confused',
    label: 'Tijdzone richting verward',
    module: 'Tijd',
    description:
      'Leerling telt uren op in plaats van af te trekken bij tijdzones, of andersom.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },
  {
    id: 'tijd.timezone-midnight-error',
    label: 'Tijdzone over middernacht',
    module: 'Tijd',
    description:
      'Leerling houdt geen rekening met de 24-uurs grens bij tijdzones.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },
  {
    id: 'tijd.timezone-flight-error',
    label: 'Tijdverschil vergeten bij vlucht',
    module: 'Tijd',
    description:
      'Leerling vergeet het tijdverschil op te tellen of af te trekken bij het berekenen van de aankomsttijd na een vlucht.',
    typicalFamilies: ['TIJD-TIJDZONES'],
  },

  // ── Geld (GELD) ─────────────────────────────────────────────────────────
  {
    id: 'geld.decimal-comma',
    label: 'Komma vergeten/verkeerd',
    module: 'Geld',
    description:
      'Leerling plaatst de decimale komma verkeerd bij geldbedragen.',
    typicalFamilies: ['TGL-GELD-KOMMA'],
  },
  {
    id: 'geld.change-calculation',
    label: 'Wisselgeld verkeerd berekend',
    module: 'Geld',
    description:
      'Leerling berekent wisselgeld verkeerd (trekt van het verkeerde bedrag af).',
    typicalFamilies: ['TGL-GELD-TERUG', 'TGL-GELD-TERUGREKENEN'],
  },
  {
    id: 'geld.total-vs-unit-price',
    label: 'Totaalprijs verward met eenheidsprijs',
    module: 'Geld',
    description:
      'Leerling vergelijkt totaalprijzen in plaats van de prijs per eenheid (per stuk, per kg, per liter).',
    typicalFamilies: ['TGL-GELD-KOOPJES', 'TGL-GELD-PRIJS-PER'],
  },
  {
    id: 'geld.wrong-division-direction',
    label: 'Verkeerde deelrichting bij eenheidsprijs',
    module: 'Geld',
    description:
      'Leerling deelt het aantal door de prijs in plaats van de prijs door het aantal.',
    typicalFamilies: ['TGL-GELD-KOOPJES', 'TGL-GELD-PRIJS-PER'],
  },

  // ── Taalbegrip (TAA) ────────────────────────────────────────────────────
  {
    id: 'taa.main-idea-vs-detail',
    label: 'Hoofdgedachte vs. detail',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een ondersteunend detail met de hoofdgedachte van een tekst.',
    typicalFamilies: ['TAA-LEZEN-KERN', 'TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.fact-vs-opinion',
    label: 'Feit vs. mening',
    module: 'Taalbegrip',
    description:
      'Leerling kan geen onderscheid maken tussen feiten en meningen in een tekst.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.subject-predicate-confused',
    label: 'Onderwerp/gezegde verward',
    module: 'Taalbegrip',
    description: 'Leerling verwart het onderwerp met het gezegde in een zin.',
    typicalFamilies: ['TAA-ONT-GEZEGDE', 'TAA-ONT-BASIS'],
  },

  // ── TAA-SOORT-LID misconceptions ──
  {
    id: 'taa.verb-as-noun',
    label: 'Verwart werkwoord met zelfstandig naamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een werkwoord een zelfstandig naamwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.preposition-as-noun',
    label: 'Verwart voorzetsel met zelfstandig naamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een voorzetsel een zelfstandig naamwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.article-as-noun',
    label: 'Verwart lidwoord met zelfstandig naamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een lidwoord een zelfstandig naamwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.de-het-confused',
    label: 'Verwart de-woord met het-woord',
    module: 'Taalbegrip',
    description:
      'Leerling kent het verschil tussen de-woorden en het-woorden niet.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.article-only-een',
    label: 'Denkt dat alleen een onbepaald lidwoord is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat alleen "een" een lidwoord is en vergeet de/het.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.no-article-needed',
    label: 'Denkt dat woorden geen lidwoord hebben',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat zelfstandige naamwoorden geen lidwoord nodig hebben.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.noun-undercount',
    label: 'Telt te weinig zelfstandige naamwoorden',
    module: 'Taalbegrip',
    description: 'Leerling mist zelfstandige naamwoorden bij het tellen.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.noun-overcount',
    label: 'Telt niet-naamwoorden mee als zelfstandig naamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling telt werkwoorden of voorzetsels mee als zelfstandige naamwoorden.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.noun-as-article',
    label: 'Verwart zelfstandig naamwoord met lidwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een zelfstandig naamwoord een lidwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.verb-as-article',
    label: 'Verwart werkwoord met lidwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een werkwoord een lidwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.agent-noun-as-verb',
    label: 'Verwart persoonsnaam (-er) met werkwoord',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een afgeleid naamwoord (fietser) met het werkwoord (fietsen).',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.noun-as-verb',
    label: 'Verwart zelfstandig naamwoord met werkwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een zelfstandig naamwoord een werkwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },
  {
    id: 'taa.preposition-as-verb',
    label: 'Verwart voorzetsel met werkwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een voorzetsel een werkwoord is.',
    typicalFamilies: ['TAA-SOORT-LID'],
  },

  // ── TAA-SOORT-BW misconceptions ──
  {
    id: 'taa.noun-as-adverb',
    label: 'Verwart zelfstandig naamwoord met bijwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een zelfstandig naamwoord een bijwoord is.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.verb-as-adverb',
    label: 'Verwart werkwoord met bijwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een werkwoord een bijwoord is.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.adverb-position-rule',
    label: 'Denkt dat positie in zin het verschil BNW/bijwoord bepaalt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de positie in de zin bepaalt of iets een BNW of bijwoord is.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.adverb-bnw-same',
    label: 'Denkt dat bijwoord en BNW dezelfde woordsoort zijn',
    module: 'Taalbegrip',
    description:
      'Leerling ziet geen verschil tussen bijwoord en bijvoeglijk naamwoord.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.adverb-always-first',
    label: 'Denkt dat bijwoord altijd aan begin van zin staat',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een bijwoord altijd aan het begin van de zin staat.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.bnw-as-adverb',
    label: 'Verwart BNW bij znw met bijwoord',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat een woord bij een zelfstandig naamwoord een BNW is, niet een bijwoord.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.adverb-place-as-time',
    label: 'Verwart bijwoord van plaats met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een bijwoord van plaats (waar?) met een bijwoord van tijd (wanneer?).',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.adverb-manner-as-time',
    label: 'Verwart bijwoord van wijze met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een bijwoord van wijze (hoe?) met een bijwoord van tijd (wanneer?).',
    typicalFamilies: ['TAA-SOORT-BW'],
  },
  {
    id: 'taa.adverb-degree-as-time',
    label: 'Verwart bijwoord van mate met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een bijwoord van mate met een bijwoord van tijd.',
    typicalFamilies: ['TAA-SOORT-BW'],
  },

  // ── TAA-SOORT-VNW misconceptions ──
  {
    id: 'taa.znw-as-vnw',
    label: 'Verwart zelfstandig naamwoord met voornaamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een zelfstandig naamwoord een voornaamwoord is.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.ww-as-vnw',
    label: 'Verwart werkwoord met voornaamwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een werkwoord een voornaamwoord is.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.vz-as-vnw',
    label: 'Verwart voorzetsel met voornaamwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een voorzetsel een voornaamwoord is.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.miss-zijn-bezittelijk',
    label: 'Herkent zijn niet als bezittelijk voornaamwoord',
    module: 'Taalbegrip',
    description: 'Leerling herkent "zijn" niet als bezittelijk voornaamwoord.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.haar-always-bezittelijk',
    label: 'Denkt dat haar altijd bezittelijk is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "haar" altijd een bezittelijk voornaamwoord is, ook als het persoonlijk is.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.no-bezittelijk-recognized',
    label: 'Herkent geen enkel bezittelijk voornaamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling herkent geen enkel bezittelijk voornaamwoord in een zin.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.betrekkelijk-as-aanwijzend',
    label: 'Verwart betrekkelijk met aanwijzend voornaamwoord',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een betrekkelijk voornaamwoord (die/dat na znw) met een aanwijzend voornaamwoord.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.dat-as-aanwijzend',
    label: 'Verwart dat (betrekkelijk) met aanwijzend',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "dat" in een bijzin aanwijzend is in plaats van betrekkelijk.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.betrekkelijk-after-znw',
    label: 'Herkent niet dat die na een znw betrekkelijk is',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat "die" na een zelfstandig naamwoord betrekkelijk is.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.hulpww-as-vnw',
    label: 'Verwart hulpwerkwoord met voornaamwoord',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat een hulpwerkwoord een voornaamwoord is.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.wrong-vnw-type',
    label: 'Kent juiste voornaamwoorden maar verkeerd type toe',
    module: 'Taalbegrip',
    description:
      'Leerling herkent voornaamwoorden maar wijst het verkeerde type toe (persoonlijk vs bezittelijk etc.).',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.miss-het-as-vnw',
    label: 'Vergeet het als persoonlijk voornaamwoord te tellen',
    module: 'Taalbegrip',
    description:
      'Leerling vergeet dat "het" ook een persoonlijk voornaamwoord kan zijn.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.miss-multiple-vnw',
    label: 'Telt meerdere voornaamwoorden niet mee',
    module: 'Taalbegrip',
    description: 'Leerling mist meerdere voornaamwoorden bij het tellen.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },
  {
    id: 'taa.overcount-vnw',
    label: 'Telt niet-voornaamwoorden mee',
    module: 'Taalbegrip',
    description:
      'Leerling telt voegwoorden, bijwoorden of werkwoorden mee als voornaamwoorden.',
    typicalFamilies: ['TAA-SOORT-VNW'],
  },

  // ─── TAA-SOORT-TW (telwoorden) ──────────────────────────────────────────────
  {
    id: 'taa.noun-as-numeral',
    label: 'Verwart zelfstandig naamwoord met telwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een zelfstandig naamwoord in plaats van het telwoord.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.verb-as-numeral',
    label: 'Verwart werkwoord met telwoord',
    module: 'Taalbegrip',
    description: 'Leerling kiest een werkwoord in plaats van het telwoord.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.cardinal-as-ordinal',
    label: 'Verwart hoofdtelwoord met rangtelwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een hoofdtelwoord wanneer een rangtelwoord wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.compound-as-numeral',
    label: 'Verwart samenstelling met telwoord',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat samenstellingen met getallen (drietal, driedubbel) telwoorden zijn.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.miss-ordinal-as-numeral',
    label: 'Vergeet rangtelwoorden als telwoord te tellen',
    module: 'Taalbegrip',
    description:
      'Leerling telt alleen hoofdtelwoorden en vergeet dat rangtelwoorden ook telwoorden zijn.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.miss-cardinal-as-numeral',
    label: 'Vergeet hoofdtelwoorden als telwoord te tellen',
    module: 'Taalbegrip',
    description:
      'Leerling telt alleen rangtelwoorden en vergeet hoofdtelwoorden.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.overcount-numerals',
    label: 'Telt te veel telwoorden',
    module: 'Taalbegrip',
    description: 'Leerling telt woorden mee die geen telwoorden zijn.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.cardinal-as-numeral-only',
    label: 'Denkt dat alleen exacte getallen telwoorden zijn',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat onbepaalde hoeveelheden (veel, weinig) ook telwoorden zijn, of juist dat alleen hoofdtelwoorden tellen.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },
  {
    id: 'taa.ordinal-as-numeral-only',
    label: 'Denkt dat alleen rangtelwoorden telwoorden zijn',
    module: 'Taalbegrip',
    description: 'Leerling vergeet dat hoofdtelwoorden ook telwoorden zijn.',
    typicalFamilies: ['TAA-SOORT-TW'],
  },

  // ─── TAA-SOORT-VW (voegwoorden) ─────────────────────────────────────────────
  {
    id: 'taa.preposition-as-conjunction',
    label: 'Verwart voorzetsel met voegwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een voorzetsel wanneer een voegwoord wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.article-as-conjunction',
    label: 'Verwart lidwoord met voegwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een lidwoord wanneer een voegwoord wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.adjective-as-conjunction',
    label: 'Verwart bijvoeglijk naamwoord met voegwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijvoeglijk naamwoord wanneer een voegwoord wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.contrast-as-reason',
    label: 'Verwart tegenstelling-voegwoord met reden-voegwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest maar/hoewel wanneer omdat/want wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.choice-as-reason',
    label: 'Verwart keuze-voegwoord met reden-voegwoord',
    module: 'Taalbegrip',
    description: 'Leerling kiest of wanneer omdat wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.addition-as-reason',
    label: 'Verwart toevoeging-voegwoord met reden-voegwoord',
    module: 'Taalbegrip',
    description: 'Leerling kiest en wanneer omdat wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.miss-conjunction-en',
    label: "Vergeet 'en' als voegwoord te tellen",
    module: 'Taalbegrip',
    description: "Leerling vergeet dat 'en' een voegwoord is bij het tellen.",
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.no-conjunctions-seen',
    label: 'Herkent geen voegwoorden in de zin',
    module: 'Taalbegrip',
    description:
      'Leerling ziet geen enkel voegwoord in een zin die er meerdere bevat.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.subordinating-not-conjunction',
    label: 'Denkt dat onderschikkend voegwoord geen voegwoord is',
    module: 'Taalbegrip',
    description:
      'Leerling herkent onderschikkende voegwoorden (omdat, dat, als) niet als voegwoorden.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.coordinating-not-conjunction',
    label: 'Denkt dat nevenschikkend voegwoord geen voegwoord is',
    module: 'Taalbegrip',
    description:
      'Leerling herkent nevenschikkende voegwoorden (maar, en, want) niet als voegwoorden.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },
  {
    id: 'taa.time-conjunction-not-conjunction',
    label: 'Denkt dat tijdsvoegwoord geen voegwoord is',
    module: 'Taalbegrip',
    description:
      'Leerling herkent tijdsvoegwoorden (terwijl, toen) niet als voegwoorden.',
    typicalFamilies: ['TAA-SOORT-VW'],
  },

  // ─── TAA-SOORT-VZ (voorzetsels) ─────────────────────────────────────────────
  {
    id: 'taa.verb-as-preposition',
    label: 'Verwart werkwoord met voorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een werkwoord wanneer een voorzetsel wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.article-as-preposition',
    label: 'Verwart lidwoord met voorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een lidwoord wanneer een voorzetsel wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.noun-as-preposition',
    label: 'Verwart zelfstandig naamwoord met voorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een zelfstandig naamwoord wanneer een voorzetsel wordt gevraagd.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.miss-preposition-count',
    label: 'Telt niet alle voorzetsels',
    module: 'Taalbegrip',
    description: 'Leerling mist een of meer voorzetsels bij het tellen.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.overcount-prepositions',
    label: 'Telt niet-voorzetsels mee als voorzetsel',
    module: 'Taalbegrip',
    description: 'Leerling telt lidwoorden of bijwoorden mee als voorzetsels.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.preposition-as-non-preposition',
    label: 'Denkt dat een voorzetsel geen voorzetsel is',
    module: 'Taalbegrip',
    description: 'Leerling herkent een voorzetsel niet als zodanig.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.preposition-place-as-time',
    label: 'Verwart plaatsvoorzetsel met tijdsvoorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een plaatsvoorzetsel een tijdsrelatie aangeeft.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.preposition-direction-as-time',
    label: 'Verwart richtingsvoorzetsel met tijdsvoorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een richtingsvoorzetsel een tijdsrelatie aangeeft.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },
  {
    id: 'taa.preposition-manner-as-time',
    label: 'Verwart maniervoorzetsel met tijdsvoorzetsel',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een maniervoorzetsel een tijdsrelatie aangeeft.',
    typicalFamilies: ['TAA-SOORT-VZ'],
  },

  // ─── TAA-WOORD-SYNONIEM ─────────────────────────────────────────────────────
  {
    id: 'taa.antonym-as-synonym',
    label: 'Verwart antoniem met synoniem',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het tegenovergestelde woord in plaats van een synoniem.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.related-word-as-synonym',
    label: 'Verwart verwant woord met synoniem',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord uit hetzelfde thema maar met een andere betekenis.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.near-synonym-not-synonym',
    label: 'Denkt dat bijna-synoniem geen synoniem is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat woorden met een iets andere nuance geen synoniemen zijn.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.strong-synonym-not-synonym',
    label: 'Denkt dat sterk synoniem geen synoniem is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat woorden met een sterkere betekenis geen synoniemen zijn.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },

  // ─── TAA-WOORD-CONTEXT ──────────────────────────────────────────────────────
  {
    id: 'taa.positive-word-in-negative-context',
    label: 'Kiest positief woord bij negatieve context',
    module: 'Taalbegrip',
    description:
      'Leerling negeert negatieve contextaanwijzingen en kiest een positief woord.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.underestimate-context-clue',
    label: 'Negeert contextaanwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling mist een belangrijke contextaanwijzing (bijv. "verbaasd") die de betekenis bepaalt.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.opposite-meaning-context',
    label: 'Kiest tegenovergestelde betekenis',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord met de tegenovergestelde betekenis van wat de context aangeeft.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.wrong-dimension-context',
    label: 'Verwart dimensie bij contextbetekenis',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord uit een verkeerde dimensie (bijv. snelheid i.p.v. hoeveelheid).',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.wrong-action-context',
    label: 'Kiest verkeerde handeling bij context',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een handeling die niet past bij de beschreven context.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.neutral-word-for-emotional',
    label: 'Kiest neutraal woord bij emotionele context',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een neutraal woord terwijl de context een emotionele situatie beschrijft.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },

  // ─── TAA-WOORD-IDIOOM ───────────────────────────────────────────────────────
  {
    id: 'taa.idioom-letterlijk-cadeau',
    label: 'Neemt uitdrukking letterlijk (cadeau)',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "hart onder de riem steken" als cadeau geven.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-riem',
    label: 'Neemt uitdrukking letterlijk (riem)',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "hart onder de riem steken" als riem lenen.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-steken',
    label: 'Neemt steken letterlijk (pijn)',
    module: 'Taalbegrip',
    description: 'Leerling interpreteert "steken" als pijn doen.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-buikpijn',
    label: 'Neemt uitdrukking letterlijk (buikpijn)',
    module: 'Taalbegrip',
    description: 'Leerling interpreteert "vlinders in je buik" als buikpijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-vlinders',
    label: 'Neemt uitdrukking letterlijk (echte vlinders)',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat er echte vlinders zijn gezien.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-honger',
    label: 'Verwart buikgevoel met honger',
    module: 'Taalbegrip',
    description: 'Leerling interpreteert "vlinders in je buik" als honger.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-verwisseld-boter',
    label: 'Verwart neus-in-boter met hard werken',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "met je neus in de boter vallen" (geluk) met hard werken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-verwisseld-spijker',
    label: 'Verwart spijker-op-kop met hard werken',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "de spijker op de kop slaan" (goed raden) met hard werken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-verwisseld-mand',
    label: 'Verwart door-de-mand met hard werken',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "door de mand vallen" (betrapt worden) met hard werken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-olifant-sterk',
    label: 'Neemt olifant letterlijk (sterk)',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "van een mug een olifant maken" als sterk zijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-olifant-slim',
    label: 'Interpreteert mug-olifant als slim',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "van een mug een olifant maken" als slim zijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-olifant-luid',
    label: 'Interpreteert mug-olifant als luidruchtig',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "van een mug een olifant maken" als luidruchtig zijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },

  // ─── TAA-LEZEN-KERN misconceptions ──────────────────────────────────────────
  {
    id: 'taa.detail-as-main-idea',
    label: 'Detail aanzien voor hoofdgedachte',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een detail of gevolg met de hoofdgedachte van de tekst.',
    typicalFamilies: ['TAA-LEZEN-KERN'],
  },
  {
    id: 'taa.outside-knowledge',
    label: 'Eigen kennis invullen i.p.v. tekst lezen',
    module: 'Taalbegrip',
    description:
      'Leerling baseert antwoord op eigen kennis of mening in plaats van op de tekst.',
    typicalFamilies: ['TAA-LEZEN-KERN', 'TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.adding-to-source',
    label: 'Informatie toevoegen die niet in de tekst staat',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een antwoord dat informatie bevat die niet in de brontekst staat.',
    typicalFamilies: ['TAA-LEZEN-KERN'],
  },
  {
    id: 'taa.exaggeration',
    label: 'Overdrijving van een detail uit de tekst',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een antwoord dat een detail uit de tekst overdrijft.',
    typicalFamilies: ['TAA-LEZEN-KERN'],
  },
  {
    id: 'taa.opposite-meaning',
    label: 'Tegenovergestelde van wat de tekst zegt',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een antwoord dat het tegenovergestelde beweert van de tekst.',
    typicalFamilies: ['TAA-LEZEN-KERN'],
  },
  {
    id: 'taa.partial-reading',
    label: 'Alleen een deel van de tekst gelezen',
    module: 'Taalbegrip',
    description:
      'Leerling baseert antwoord op slechts een deel van de tekst (alleen positief of negatief).',
    typicalFamilies: ['TAA-LEZEN-KERN'],
  },
  {
    id: 'taa.title-too-vague',
    label: 'Titel te vaag — dekt de kern niet',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een titel die te breed of vaag is om de inhoud te dekken.',
    typicalFamilies: ['TAA-LEZEN-KERN'],
  },

  // ─── TAA-LEZEN-DETAIL misconceptions ────────────────────────────────────────
  {
    id: 'taa.wrong-number-from-text',
    label: 'Verkeerd getal uit tekst gekozen',
    module: 'Taalbegrip',
    description:
      'Leerling koppelt het verkeerde getal aan de gevraagde informatie (bijv. bouwjaar i.p.v. hoogte).',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.imprecise-reading',
    label: 'Tekst onnauwkeurig gelezen',
    module: 'Taalbegrip',
    description:
      'Leerling leest een getal of woord onnauwkeurig (bijv. 300 i.p.v. 330).',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.synonym-confusion',
    label: 'Verwart vergelijkbare maar verschillende termen',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een term met een vergelijkbaar maar ander woord (bijv. blaasgat vs. neus).',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.misleading-detail',
    label: 'Afleidende informatie uit tekst',
    module: 'Taalbegrip',
    description:
      'Leerling laat zich afleiden door een detail dat lijkt op het antwoord maar het niet is (bijv. heviger ≠ meer).',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.answer-not-in-text',
    label: 'Antwoord niet in tekst',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een antwoord dat niet in de tekst wordt genoemd.',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.confuses-old-and-modern',
    label: 'Verwart eerste en moderne variant',
    module: 'Taalbegrip',
    description:
      'Leerling verwart informatie over de oorspronkelijke en moderne versie van iets.',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },
  {
    id: 'taa.wrong-question-type',
    label: 'Antwoordt op verkeerd vraagtype',
    module: 'Taalbegrip',
    description:
      'Leerling beantwoordt een waar-vraag met wanneer of hoe vaak, of andersom.',
    typicalFamilies: ['TAA-LEZEN-DETAIL'],
  },

  // ─── TAA-LEZEN-FEIT misconceptions ──────────────────────────────────────────
  {
    id: 'taa.feit-mening-regels-als-feiten',
    label: 'Verwart spelregels (feiten) met meningen',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat controleerbare spelregels meningen zijn.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-historie-als-feit',
    label: 'Verwart historische feiten met meningen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat controleerbare historische gebeurtenissen meningen zijn.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-aantallen-als-feiten',
    label: 'Verwart controleerbare aantallen met meningen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat controleerbare getallen of aantallen meningen zijn.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-overtr-trap',
    label: 'Herkent overtreffende trap niet als meningssignaal',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat de overtreffende trap (het mooiste, het beste) een waardeoordeel is.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-zou-moeten',
    label: 'Herkent "zou moeten" niet als meningssignaal',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat "zou moeten" een persoonlijke overtuiging aangeeft.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-superlative',
    label: 'Herkent superlative niet als waardeoordeel',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat "het fijnste", "het leukste" etc. waardeoordelen zijn.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-twee-feiten',
    label: 'Verwart twee feiten in één zin met feit+mening',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een zin met twee controleerbare feiten automatisch feit+mening bevat.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-ik-vind-geen-feit',
    label: 'Denkt dat "ik vind" automatisch feit+mening maakt',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "ik vind" in een zin automatisch betekent dat er ook een feit in staat.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-sterke-bewering',
    label: 'Verwart sterke bewering met feit+mening',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een sterke bewering (de beste) automatisch een feit bevat.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.signaal-opsomming-vs-mening',
    label: 'Verwart opsommingssignaalwoord met meningssignaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "ten eerste" (opsomming) met een meningssignaalwoord.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.signaal-voorbeeld-vs-mening',
    label: 'Verwart voorbeeldsignaalwoord met meningssignaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "bijvoorbeeld" (voorbeeld) met een meningssignaalwoord.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.signaal-uitleg-vs-mening',
    label: 'Verwart uitlegsignaalwoord met meningssignaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "namelijk" (uitleg) met een meningssignaalwoord.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-data-als-feit',
    label: 'Verwart controleerbare data met mening',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat controleerbare data (jaartallen, getallen) meningen zijn.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-werkstuk-alles-feit',
    label: 'Denkt dat alles in een werkstuk een feit is',
    module: 'Taalbegrip',
    description:
      'Leerling gaat ervan uit dat alles in een werkstuk automatisch een feit is.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },
  {
    id: 'taa.feit-mening-alles-mening',
    label: 'Denkt dat alles een mening is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat alle uitspraken meningen zijn, ook controleerbare feiten.',
    typicalFamilies: ['TAA-LEZEN-FEIT'],
  },

  // ─── TAA-LEZEN-DOEL misconceptions ──────────────────────────────────────────
  {
    id: 'taa.doel-informeren-vs-overtuigen',
    label: 'Verwart informeren met overtuigen',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat een oproep of mening wijst op overtuigen, niet informeren.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-vermaken-vs-overtuigen',
    label: 'Verwart vermaken met overtuigen',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een overtuigende tekst met een vermakelijke tekst.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-waarschuwen-vs-overtuigen',
    label: 'Verwart waarschuwen met overtuigen',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een overtuigende tekst met een waarschuwende tekst.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-overtuigen-vs-informeren',
    label: 'Verwart overtuigen met informeren',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een neutrale informatieve tekst overtuigend is.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-vermaken-vs-informeren',
    label: 'Verwart vermaken met informeren',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een informatieve tekst met een vermakelijke tekst.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-instrueren-vs-informeren',
    label: 'Verwart instrueren met informeren',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een informatieve tekst met een instructietekst.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.signaal-opsomming-vs-overtuigen',
    label: 'Verwart opsommingssignaal met overtuigingssignaal',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "ten eerste" een overtuigingssignaalwoord is.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.signaal-verhaal-vs-overtuigen',
    label: 'Verwart verhaalsignaal met overtuigingssignaal',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "er was eens" een overtuigingssignaalwoord is.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.signaal-instructie-vs-overtuigen',
    label: 'Verwart instructiesignaal met overtuigingssignaal',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "voeg toe" een overtuigingssignaalwoord is.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-neutraal-als-overtuigen-voor',
    label: 'Denkt dat voor+nadelen = overtuigen vóór',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een neutrale tekst met voor- en nadelen overtuigt vóór het onderwerp.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },
  {
    id: 'taa.doel-neutraal-als-overtuigen-tegen',
    label: 'Denkt dat voor+nadelen = overtuigen tegen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een neutrale tekst met voor- en nadelen overtuigt tegen het onderwerp.',
    typicalFamilies: ['TAA-LEZEN-DOEL'],
  },

  // ─── TAA-LEZEN-SAMENVATTEN misconceptions ──────────────────────────────────
  {
    id: 'taa.samenvatting-te-smal',
    label: 'Kiest detail i.p.v. samenvatting',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een detail als samenvatting in plaats van de overkoepelende hoofdgedachte.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },
  {
    id: 'taa.samenvatting-hoofdgedachte-weglaten',
    label: 'Denkt dat hoofdgedachte niet in samenvatting hoort',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de hoofdgedachte niet in een samenvatting thuishoort.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },
  {
    id: 'taa.samenvatting-punten-weglaten',
    label: 'Denkt dat belangrijkste punten niet in samenvatting horen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de belangrijkste punten per alinea niet in een samenvatting horen.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },
  {
    id: 'taa.samenvatting-conclusie-weglaten',
    label: 'Denkt dat conclusie niet in samenvatting hoort',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat de conclusie van de schrijver niet in een samenvatting hoort.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },
  {
    id: 'taa.samenvatting-eigen-mening',
    label: 'Denkt dat samenvatten begint met eigen mening',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat je bij samenvatten begint met je eigen mening opschrijven.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },
  {
    id: 'taa.samenvatting-alle-details',
    label: 'Denkt dat alle details opgeschreven moeten worden',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een samenvatting alle details moet bevatten.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },
  {
    id: 'taa.samenvatting-overschrijven',
    label: 'Verwart samenvatten met overschrijven',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat samenvatten hetzelfde is als de tekst letterlijk overschrijven.',
    typicalFamilies: ['TAA-LEZEN-SAMENVATTEN'],
  },

  // ─── TAA-LEZEN-STRUCTUUR misconceptions ─────────────────────────────────────
  {
    id: 'taa.structuur-oorzaak-vs-probleem',
    label: 'Verwart oorzaak-gevolg met probleem-oplossing',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de oorzaak-gevolgstructuur met de probleem-oplossingstructuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-vergelijking-vs-probleem',
    label: 'Verwart vergelijking met probleem-oplossing',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de vergelijkingsstructuur met de probleem-oplossingstructuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-chronologisch-vs-probleem',
    label: 'Verwart chronologisch met probleem-oplossing',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de chronologische structuur met de probleem-oplossingstructuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.signaal-opsomming-vs-oorzaak',
    label: 'Verwart opsommingssignaal met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "ten eerste" een oorzaak-gevolg signaalwoord is.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.signaal-tegenstelling-vs-oorzaak',
    label: 'Verwart tegenstellingssignaal met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "in tegenstelling tot" een oorzaak-gevolg signaalwoord is.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.signaal-chronologisch-vs-oorzaak',
    label: 'Verwart chronologisch signaal met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "ten slotte" een oorzaak-gevolg signaalwoord is.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-opsomming-vs-vergelijking',
    label: 'Verwart opsomming met vergelijking',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de opsommingsstructuur met de vergelijkingsstructuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-probleem-vs-vergelijking',
    label: 'Verwart probleem-oplossing met vergelijking',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de probleem-oplossingstructuur met de vergelijkingsstructuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-oorzaak-vs-vergelijking',
    label: 'Verwart oorzaak-gevolg met vergelijking',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de oorzaak-gevolgstructuur met de vergelijkingsstructuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-oorzaak-vs-chronologisch',
    label: 'Verwart oorzaak-gevolg met chronologisch',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de oorzaak-gevolgstructuur met de chronologische structuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-vergelijking-vs-chronologisch',
    label: 'Verwart vergelijking met chronologisch',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de vergelijkingsstructuur met de chronologische structuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  {
    id: 'taa.structuur-probleem-vs-chronologisch',
    label: 'Verwart probleem-oplossing met chronologisch',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de probleem-oplossingstructuur met de chronologische structuur.',
    typicalFamilies: ['TAA-LEZEN-STRUCTUUR'],
  },
  // ── TAA-LEZEN-VERB misconceptions ──
  {
    id: 'taa.verb-tegenstelling-vs-oorzaak',
    label: 'Verwart tegenstelling met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een tegenstellingssignaalwoord (maar, toch) een oorzaak-gevolg aangeeft.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-tegenstelling-vs-gevolg',
    label: 'Verwart tegenstelling met gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een gevolg-signaalwoord (dus) als tegenstelling.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-tegenstelling-vs-toevoeging',
    label: 'Verwart tegenstelling met toevoeging',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een toevoegingswoord (ook) een tegenstelling aangeeft.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-tegenstelling-vs-opsomming',
    label: 'Verwart tegenstelling met opsomming',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een tegenstellingsverband met een opsommingsverband.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-tegenstelling-vs-tijdsvolgorde',
    label: 'Verwart tegenstelling met tijdsvolgorde',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een tegenstellingsverband met een tijdsvolgordeverband.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-oorzaak-vs-tegenstelling',
    label: 'Verwart oorzaak-gevolg met tegenstelling',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een oorzaak-gevolgsignaalwoord (daardoor) een tegenstelling aangeeft.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-oorzaak-vs-vergelijking',
    label: 'Verwart oorzaak-gevolg met vergelijking',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een oorzaak-gevolgverband met een vergelijkingsverband.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-oorzaak-vs-opsomming',
    label: 'Verwart oorzaak-gevolg met opsomming',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een oorzaak-gevolgverband met een opsommingsverband.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-opsomming-vs-tegenstelling',
    label: 'Verwart opsomming met tegenstelling',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een opsommingssignaalwoord (bovendien) een tegenstelling aangeeft.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-opsomming-vs-oorzaak',
    label: 'Verwart opsomming met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een opsommingssignaalwoord een oorzaak-gevolg aangeeft.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  {
    id: 'taa.verb-opsomming-vs-tijdsvolgorde',
    label: 'Verwart opsomming met tijdsvolgorde',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een opsommingssignaalwoord een tijdsvolgorde aangeeft.',
    typicalFamilies: ['TAA-LEZEN-VERB'],
  },
  // ── TAA-LEZEN-VERW misconceptions ──
  {
    id: 'taa.verw-daar-naar-persoon',
    label: 'Verwart plaatsverwijswoord met persoonsverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "daar" naar een persoon verwijst in plaats van naar een plaats.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-daar-naar-object',
    label: 'Verwart plaatsverwijswoord met objectverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "daar" naar een voorwerp verwijst in plaats van naar een plaats.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-ze-naar-plaats',
    label: 'Verwart persoonsverwijzing met plaatsverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "ze" naar een plaats verwijst in plaats van naar personen.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-ze-naar-tijd',
    label: 'Verwart persoonsverwijzing met tijdsaanduiding',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat "ze" naar een tijdsaanduiding verwijst.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-hem-naar-onderwerp',
    label: 'Verwart lijdend-voorwerp-verwijzing met onderwerpverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "hem" naar het onderwerp verwijst terwijl "hij" dat al doet.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-hem-niet-in-tekst',
    label: 'Verwijst naar woord buiten de tekst',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een verwijswoord naar iets verwijst dat niet in de tekst staat.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-dat-naar-persoon',
    label: 'Verwart "dat" met persoonsverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "dat" naar een persoon verwijst in plaats van naar een handeling.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-dat-naar-object-ipv-handeling',
    label: 'Verwart "dat" als objectverwijzing i.p.v. handelingsverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "dat" naar een enkel object verwijst terwijl het naar een hele handeling verwijst.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-zij-enkelvoud-vs-meervoud',
    label: 'Verwart "zij" enkelvoud met meervoud',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat "zij" zowel enkelvoud (vrouwelijk) als meervoud kan zijn.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  {
    id: 'taa.verw-zij-naar-niet-persoon',
    label: 'Verwart "zij" met niet-persoonsverwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat "zij" naar een voorwerp of begrip verwijst in plaats van naar personen.',
    typicalFamilies: ['TAA-LEZEN-VERW'],
  },
  // ── TAA-LEZEN-VOORSPEL misconceptions ──
  {
    id: 'taa.voorspel-enkele-aanwijzing',
    label: 'Baseert voorspelling op één aanwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling baseert de voorspelling op slechts één aanwijzing en negeert de overige.',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  {
    id: 'taa.voorspel-extreme-conclusie',
    label: 'Trekt een te extreme conclusie',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een extreme of onwaarschijnlijke voorspelling in plaats van de meest logische.',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  {
    id: 'taa.voorspel-negatief-bias',
    label: 'Negatieve bias bij voorspellen',
    module: 'Taalbegrip',
    description:
      'Leerling verwacht een negatieve uitkomst terwijl de aanwijzingen positief zijn.',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  {
    id: 'taa.voorspel-niet-in-tekst',
    label: 'Voorspelling niet gebaseerd op tekst',
    module: 'Taalbegrip',
    description:
      'Leerling maakt een voorspelling die niet ondersteund wordt door aanwijzingen in de tekst.',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  {
    id: 'taa.voorspel-genre-verwarring',
    label: 'Verwart tekstgenre bij voorspellen',
    module: 'Taalbegrip',
    description:
      'Leerling verwart het genre van de tekst (verhalend vs instructief vs wetenschappelijk).',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  {
    id: 'taa.voorspel-titel-letterlijk',
    label: 'Neemt titel te letterlijk',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert de titel te letterlijk zonder de bredere betekenis te zien.',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  {
    id: 'taa.voorspel-aanwijzing-negeren',
    label: 'Negeert relevante aanwijzingen',
    module: 'Taalbegrip',
    description:
      'Leerling negeert een of meer relevante aanwijzingen bij het maken van een voorspelling.',
    typicalFamilies: ['TAA-LEZEN-VOORSPEL'],
  },
  // ── TAA-LEZEN-CONCLUSIE misconceptions ──
  {
    id: 'taa.conclusie-overgeneralisatie',
    label: 'Overgeneraliseert bij conclusie',
    module: 'Taalbegrip',
    description:
      'Leerling trekt een te brede conclusie die verder gaat dan de feiten ondersteunen.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-niet-in-tekst',
    label: 'Conclusie niet gebaseerd op tekst',
    module: 'Taalbegrip',
    description:
      'Leerling trekt een conclusie die niet ondersteund wordt door de gegeven feiten.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-detail-vs-conclusie',
    label: 'Verwart detail met conclusie',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een detail uit de tekst in plaats van een logische conclusie.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-voorspelling-vs-conclusie',
    label: 'Verwart voorspelling met conclusie',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een voorspelling (wat gaat er gebeuren?) met een conclusie (wat volgt uit de feiten?).',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-geldig-vs-ongeldig',
    label: 'Herkent ongeldige conclusie niet',
    module: 'Taalbegrip',
    description:
      'Leerling herkent niet dat een conclusie ongeldig is omdat deze niet uit de feiten volgt.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-exclusiviteit',
    label: 'Voegt exclusiviteit toe aan conclusie',
    module: 'Taalbegrip',
    description:
      'Leerling concludeert dat iets "alleen" of "het beste" is terwijl de tekst dat niet zegt.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-mening-vs-conclusie',
    label: 'Verwart mening met conclusie',
    module: 'Taalbegrip',
    description:
      'Leerling verwart een persoonlijke mening met een logische conclusie uit feiten.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
  {
    id: 'taa.conclusie-altijd-waar',
    label: 'Denkt dat conclusies altijd waar zijn',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat een conclusie per definitie waar is, terwijl de redenering ook fout kan zijn.',
    typicalFamilies: ['TAA-LEZEN-CONCLUSIE'],
  },
]

// ─── Lookup helpers ───────────────────────────────────────────────────────────

const _byId = new Map<string, MisconceptionDefinition>()
for (const m of MISCONCEPTION_REGISTRY) {
  _byId.set(m.id, m)
}

/** Get a misconception definition by ID. Returns undefined if not found. */
export function getMisconception(
  id: string,
): MisconceptionDefinition | undefined {
  return _byId.get(id)
}

/** Check if a misconception ID exists in the registry. */
export function isKnownMisconception(id: string): boolean {
  return _byId.has(id)
}

/** Get all misconceptions for a given module. */
export function getMisconceptionsForModule(
  module: string,
): MisconceptionDefinition[] {
  return MISCONCEPTION_REGISTRY.filter((m) => m.module === module)
}

/** Get all unique misconception IDs. */
export function getAllMisconceptionIds(): string[] {
  return MISCONCEPTION_REGISTRY.map((m) => m.id)
}
